"use client";

import { createContext, useContext } from "react";

/**
 * The app-level owner of the session-start chooser overlay — the fork shown
 * when the user starts a new session with NO team context (§5.5). The chooser
 * is a menu, so opening it takes no auth gate; each option applies its own
 * (the "New team" option gates before opening the team modal).
 *
 * `openAllTeamsGallery` is the "Existing team" option's destination while the
 * landing is mounted: the chooser host renders BESIDE the route subtree, not
 * inside it, so it cannot reach the landing's gallery state on its own. The
 * landing lends its own "All teams" opener through `setOpenAllTeamsGallery`
 * and takes it back on unmount (the session-UI `setOpenTimeMachine` idiom), so
 * the lent opener is the hero CTA's handler — never a second way in. Off the
 * landing there is no opener and the option navigates instead; the HOST owns
 * that fork, this carrier only holds what the landing lent.
 */
export interface SessionStartChooserContextValue {
  isSessionStartChooserOpen: boolean;
  openSessionStartChooser: () => void;
  closeSessionStartChooser: () => void;
  openAllTeamsGallery: () => void;
  setOpenAllTeamsGallery: (openAllTeamsGallery: (() => void) | null) => void;
}

export const SessionStartChooserContext =
  createContext<SessionStartChooserContextValue | null>(null);

export function useSessionStartChooser(): SessionStartChooserContextValue {
  const context = useContext(SessionStartChooserContext);
  if (context == null) {
    throw new Error(
      "useSessionStartChooser must be used within a SessionStartChooserProvider",
    );
  }
  return context;
}
